import { useWhenVisible } from '@/hooks/useWhenVisible';
import React, { useEffect } from 'react';

/**
 * A React component that checks the visibility of a span element and triggers a callback when the visibility changes.
 * This is not useful for functional components since you can use the `useWhenVisible` hook directly.
 * Use this only with class components.
 *
 * Usage : put this component in the render method of a class component and pass the callback function to the `onVisibilityChange` prop at the position where you want to check the visibility.
 *
 * @param {Object} props - The component props.
 * @param {(isVisible: boolean) => void} props.onVisibilityChange - A callback function that is called with the visibility status of the span element.
 *
 * @returns {JSX.Element} A span element that is monitored for visibility changes.
 */
const VisibilityChecker = ({ onVisibilityChange }: { onVisibilityChange: (isVisible: boolean) => void }) => {
  const [ref, isVisible] = useWhenVisible<HTMLSpanElement>(false);
  useEffect(() => {
    onVisibilityChange(isVisible);
  }, [isVisible, onVisibilityChange]);

  return <span ref={ref} />;
};

export default VisibilityChecker;
